"use client";

import React, {useRef, useState, useMemo, useCallback, useEffect, useLayoutEffect} from "react";

import Image from "next/image";
import Chip from "@/components/global/ui/Chip";
import CrosshairOverlay from "@/components/global/ui/CrosshairOverlay";
import CursorBubble from "@/components/global/ui/CursorBubble";
import {motion} from "framer-motion";
import BackgroundVideo from "@/components/global/utils/BackgroundVideo";
import {cn} from "@/lib/utils";
import {PortableText} from "@portabletext/react";
import {richTextComponent} from "@/sanity/sanityPortableText";
import {CTACornerButton} from "@/shadcn/ui/corner-button";
import {HLSVideoPlayer} from "@/components/ui/SanityVideoPlayer";
import gsap from "gsap";
import {ScrollTrigger} from "gsap/ScrollTrigger";
gsap.registerPlugin(ScrollTrigger);

import {PageLiveCallSchema} from "@/sanity/pages/PageLiveCallTemplate";

import grid from "@/public/live-call/hero-grid.svg";
import videoOverlayFallback from "@/public/live-call/hero-fallback.png";

export default function HeroSection({
  data,
  className
}: {
  data: PageLiveCallSchema;
  className?: string;
}) {
  const sectionRef = useRef<HTMLElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const pauseTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isTouchDevice = typeof window !== "undefined" && window.matchMedia("(pointer: coarse)").matches;

  const hours = useMemo(() =>
    Array.from({length: 24}, (_, i) => `${1 + i}:00`)
  , []);

  const yRange = useMemo(() =>
    ({ min: 1.245, max: 1.560, step: 0.015, decimals: 3 })
  , []);

  const yLabels = useMemo(() =>
    Array.from(
      { length: Math.round((yRange.max - yRange.min) / yRange.step) + 1 },
      (_, i) => (yRange.min + i * yRange.step).toFixed(yRange.decimals)
    )
  , [yRange]);

  const [activeYIndex, setActiveYIndex] = useState(-1);
  const [mobileHoverActive, setMobileHoverActive] = useState(false);
  const [desktopHoverActive, setDesktopHoverActive] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(
    typeof window !== "undefined" ? window.innerWidth < 993 : false
  );
  const isFirstEnterRef = useRef(true);

  useEffect(() => {
    const checkScreenSize = () => setIsSmallScreen(window.innerWidth < 993);

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  useLayoutEffect(() => {
    if (!isTouchDevice) return;
    const el = sectionRef.current;
    if (!el) return;

    const st = ScrollTrigger.create({
      trigger: el,
      start: "top bottom",
      end: "bottom top",
      onEnter: () => {
        if (isFirstEnterRef.current) {
          setTimeout(() => setMobileHoverActive(true), 1500);
          isFirstEnterRef.current = false;
        } else {
          setMobileHoverActive(true);
        }
      },
      onLeave: () => setMobileHoverActive(false),
      onEnterBack: () => {
        setMobileHoverActive(true);
      },
      onLeaveBack: () => setMobileHoverActive(false),
      invalidateOnRefresh: true,
    });

    return () => st.kill();
  }, [isTouchDevice]);

  const handleYChange = useCallback((raw: number, snapped: number, idx: number) => {
    setActiveYIndex(idx);
  }, []);

  const playVideo = useCallback(() => {
    if (pauseTimeoutRef.current) {
      clearTimeout(pauseTimeoutRef.current);
      pauseTimeoutRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.play().catch((error) => {
        if (error.name !== 'AbortError') {
          console.error('Video play failed:', error);
        }
      });
    }
  }, []);

  const pauseVideo = useCallback(() => {
    if (pauseTimeoutRef.current) {
      clearTimeout(pauseTimeoutRef.current);
    }

    pauseTimeoutRef.current = setTimeout(() => {
      if (videoRef.current) {
        videoRef.current.pause();
      }
      pauseTimeoutRef.current = null;
    }, 500);
  }, []);

  useEffect(() => {
    const isActive = isTouchDevice ? mobileHoverActive : desktopHoverActive;

    if (isActive) {
      playVideo();
    } else {
      pauseVideo();
    }
  }, [mobileHoverActive, desktopHoverActive, isTouchDevice, playVideo, pauseVideo]);

  const handleMouseEnter = () => {
    if (!isTouchDevice) {
      setDesktopHoverActive(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isTouchDevice) {
      setDesktopHoverActive(false);
    }
  };

  return (
    <section
      ref={sectionRef}
      className={cn(
        "relative overflow-clip bg-[#0A1A1D] lg:min-h-[115rem] pt-[7rem] lg:pt-[9.5rem] pb-[8rem] lg:pb-[17.75rem]",
        className
      )}
    >
      <Image
        className="absolute left-1/2 top-0 -translate-x-1/2 !w-[max(243.25rem,101%)] !max-w-[unset] pointer-events-none"
        src={grid}
        alt="background grid shapes"
      />

      <div className="container relative flex flex-col items-center">
        <div className="w-[min(168.5rem,100%)] relative flex flex-col items-center mb-[3rem] lg:mb-[5.5rem] group">
          <div className="flex flex-col items-center lg:absolute lg:left-1/2 lg:top-1/2 lg:-translate-x-1/2 lg:-translate-y-1/2 mb-[3rem] lg:mb-0 lg:z-10">
            <Chip
              className="mb-[1.5rem]"
              image={data?.heroChip?.icon}
              text={data?.heroChip?.text}
            />

            {data?.heroTitle && (
              <div className="richtext prose-h1:text-[7rem] lg:prose-h1:text-[8rem] prose-h1:text-white prose-p:text-white/60 prose-p:text-[2.125rem] lg:prose-p:text-[2.25rem] prose-p:w-[min(69.625rem,100%)] prose-em:font-ivy prose-em:font-light prose-em:tracking-[0.3px]">
                <PortableText
                  value={data.heroTitle}
                  components={richTextComponent}
                />
              </div>
            )}

            {data?.heroCTA && (
              <CTACornerButton
                className="mt-[1.5rem]"
                data={data.heroCTA}
              />
            )}
          </div>

          <motion.div
            className="w-full aspect-video lg:aspect-[1347/576] border relative mix-blend-screen lg:border-[#D9F9B9]/25 lg:group-hover:border-[#D9F9B9]/80 lg:group-hover:scale-[1.02] lg:transition-all lg:duration-500"
            initial={isTouchDevice ? { borderColor: "rgba(217, 249, 185, 0.25)", scale: 1 } : false}
            animate={isTouchDevice ? (mobileHoverActive ? {
              borderColor: "rgba(217, 249, 185, 0.8)",
              scale: 1.02
            } : {
              borderColor: "rgba(217, 249, 185, 0.25)",
              scale: 1
            }) : undefined}
            transition={{ duration: 0.5 }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <motion.div
              className="absolute left-0 top-0 !w-full !h-full lg:opacity-0 lg:group-hover:opacity-20 lg:transition-opacity lg:duration-500"
              initial={isTouchDevice ? { opacity: 0 } : false}
              animate={isTouchDevice ? (mobileHoverActive ? { opacity: 0.4 } : { opacity: 0 }) : undefined}
              transition={{ duration: 0.5 }}
            >
              <HLSVideoPlayer
                ref={videoRef}
                source={data.heroVideo}
                autoPlay={false}
                muted
                loop
                playsInline
                className="w-full h-full object-cover"
              />
            </motion.div>

            <motion.div
              className="absolute inset-0 lg:opacity-10 lg:group-hover:opacity-[.035] lg:transition-opacity lg:duration-500"
              initial={isTouchDevice ? { opacity: 0.1 } : false}
              animate={isTouchDevice ? (mobileHoverActive ? { opacity: 0.035 } : { opacity: 0.1 }) : undefined}
              transition={{ duration: 0.5 }}
            >
              <BackgroundVideo
                playbackId="j431fQGZ8ynwnB6LF8oqNf5aJdVJypYkG01VfXQCZfB00"
                fallbackImage={videoOverlayFallback}
              />
            </motion.div>

            <div className="bg-[#D9F9B9] h-[.75rem] w-[.75rem] lg:h-[1rem] lg:w-[1rem] absolute top-0 left-0 -translate-y-1/2 -translate-x-1/2"/>
            <div className="bg-[#D9F9B9] h-[.75rem] w-[.75rem] lg:h-[1rem] lg:w-[1rem] absolute top-0 right-0 -translate-y-1/2 translate-x-1/2"/>
            <div className="bg-[#D9F9B9] h-[.75rem] w-[.75rem] lg:h-[1rem] lg:w-[1rem] absolute bottom-0 right-0 translate-y-1/2 translate-x-1/2"/>
            <div className="bg-[#D9F9B9] h-[.75rem] w-[.75rem] lg:h-[1rem] lg:w-[1rem] absolute bottom-0 left-0 translate-y-1/2 -translate-x-1/2"/>

            <motion.div
              className={cn(
                "absolute",
                !isTouchDevice && "lg:left-[calc(100%+6.5rem)] lg:top-[8.5rem] opacity-0 lg:group-hover:left-[calc(100%-3rem)] lg:group-hover:top-[14rem] lg:group-hover:opacity-100 lg:transition-all lg:duration-500"
              )}
              initial={isTouchDevice ? (isSmallScreen
                ? { left: "50%", top: "calc(100% + 2rem)", opacity: 0 }
                : { left: "calc(100% + 6.5rem)", top: "8.5rem", opacity: 0 }
              ) : false}
              animate={isTouchDevice ? (mobileHoverActive ? (isSmallScreen
                ? { left: "70%", top: "90%", opacity: 1 }
                : { left: "calc(100% - 3rem)", top: "14rem", opacity: 1 }
              ) : (isSmallScreen
                ? { left: "50%", top: "calc(100% + 2rem)", opacity: 0 }
                : { left: "calc(100% + 6.5rem)", top: "8.5rem", opacity: 0 }
              )) : undefined}
              transition={{ duration: 0.5 }}
            >
              <CursorBubble
                className="relative"
                name="Julian"
                color="#E9B9F9"
              />
            </motion.div>

            <motion.div
              className={cn(
                "absolute",
                !isTouchDevice && "lg:-bottom-[11.25rem] lg:left-0 lg:-translate-x-full opacity-0 lg:group-hover:bottom-[13.5rem] lg:group-hover:-translate-x-[60%] lg:group-hover:opacity-100 lg:transition-all lg:duration-500"
              )}
              initial={isTouchDevice ? (isSmallScreen
                ? { bottom: "-5rem", left: 0, x: "-150%", opacity: 0 }
                : { bottom: "-11.25rem", left: 0, x: "-100%", opacity: 0 }
              ) : false}
              animate={isTouchDevice ? (mobileHoverActive ? (isSmallScreen
                ? { bottom: "13.5rem", left: 0, x: "35%", opacity: 1 }
                : { bottom: "13.5rem", left: 0, x: "-60%", opacity: 1 }
              ) : (isSmallScreen
                ? { bottom: "-5rem", left: 0, x: "-150%", opacity: 0 }
                : { bottom: "-11.25rem", left: 0, x: "-100%", opacity: 0 }
              )) : undefined}
              transition={{ duration: 0.5 }}
            >
              <CursorBubble
                className="relative"
                name="Phillip"
                color="#F9E0B9"
              />
            </motion.div>
          </motion.div>
        </div>

        {data?.heroText && (
          <div className="richtext prose-p:text-white/60 prose-p:text-[2rem] lg:prose-p:text-[2.25rem] w-[min(96rem,100%)]">
            <PortableText
              value={data.heroText}
              components={richTextComponent}
            />
          </div>
        )}
      </div>

      {/* x axis numbers */}
      <div className="hidden lg:block absolute bottom-0 left-0 right-[8rem] pb-[2rem]">
        <div className="flex justify-end gap-[8.75rem]">
          {hours.map((h, index) => (
            <p
              key={index}
              className="mb-0 text-[2rem] text-white/40 leading-none tabular-nums flex-shrink-0"
            >
              {h}
            </p>
          ))}
        </div>
      </div>

      {/* y axis numbers */}
      <div className="hidden lg:flex absolute h-full right-[1.5rem] bottom-[6rem] w-[6rem] flex-col-reverse justify-start items-end gap-[3rem] pointer-events-none">
        {yLabels.map((value, index) => (
          <p
            key={index}
            className="mb-0 text-[2rem] text-white/40 leading-none tabular-nums flex-shrink-0"
          >
            {value}
          </p>
        ))}
      </div>

      {!isTouchDevice && (
        <CrosshairOverlay
          parentRef={sectionRef}
          yRange={yRange}
          onYChange={handleYChange}
        />
      )}
    </section>
  );
}
